'use client';

import { useCallback, useEffect, useState } from 'react';
import type { DashboardLayout } from '../widgets/types';

const STORAGE_KEY = 'sf-dashboard-layout';

export function useDashboardLayout(defaultLayout: DashboardLayout) {
  const [layout, setLayout] = useState<DashboardLayout>(defaultLayout);
  const [hydrated, setHydrated] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DashboardLayout;
        if (parsed && Array.isArray(parsed.visible) && Array.isArray(parsed.hidden)) {
          setLayout(parsed);
        }
      }
    } catch {}
    setHydrated(true);
  }, []);

  // Persist on change (after hydration to avoid overwriting)
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
    } catch {}
  }, [layout, hydrated]);

  const setOrder = useCallback((newOrder: string[]) => {
    setLayout((prev) => ({ ...prev, visible: newOrder }));
  }, []);

  const hideWidget = useCallback((id: string) => {
    setLayout((prev) => ({
      visible: prev.visible.filter((v) => v !== id),
      hidden: prev.hidden.includes(id) ? prev.hidden : [...prev.hidden, id],
      overrides: prev.overrides,
    }));
  }, []);

  const showWidget = useCallback((id: string) => {
    setLayout((prev) => ({
      visible: prev.visible.includes(id) ? prev.visible : [...prev.visible, id],
      hidden: prev.hidden.filter((h) => h !== id),
      overrides: prev.overrides,
    }));
  }, []);

  const reset = useCallback(() => {
    setLayout(defaultLayout);
  }, [defaultLayout]);

  return {
    layout,
    hydrated,
    setOrder,
    hideWidget,
    showWidget,
    reset,
  };
}
